import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import React, { useEffect, useState } from "react";
import Input from "components/shared/inputs/input";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import { useUpdateYearLossMutation } from "store/api";
import type { DatasourceMutationRequest } from "./TvaPopup";

type EditableYearLossRow = {
  id?: number | string;
  code?: string;
  envyear?: number | string;
  envvalue?: number | string;
};

type YearLossPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  request: DatasourceMutationRequest | null;
  envNumeric: number | undefined;
  row?: EditableYearLossRow | null;
  onSaved?: () => Promise<void> | void;
};

const YearLossPopup: React.FC<YearLossPopupProps> = ({
  isOpen,
  onClose,
  request,
  envNumeric,
  row,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [updateYearLoss] = useUpdateYearLossMutation();

  const { control, reset, getValues } = useCustomForm<{
    envyear: string;
    envvalue: string;
  }>({
    defaultValues: { envyear: "", envvalue: "" },
  });

  useEffect(() => {
    setIsSaving(false);
    reset({
      envyear: isOpen ? String(row?.envyear ?? "") : "",
      envvalue: isOpen ? String(row?.envvalue ?? "") : "",
    });
  }, [isOpen, reset, row?.envyear, row?.envvalue]);

  const handleSave = async () => {
    if (!request || envNumeric == null) return;
    const rowId = row?.id ?? (row as any)?.key;
    if (rowId == null) return;

    const { envyear, envvalue } = getValues();
    const baseUrl = request.url.replace(/\/[^/]+$/, '');
    const url = `${baseUrl}/${rowId}`;

    setIsSaving(true);
    try {
      await updateYearLoss({
        url,
        data: {
          id: rowId,
          code: row?.code,
          envyear,
          envvalue,
          env: String(envNumeric),
        },
      }).unwrap();

      onSaved?.();
      onClose();
    //   globalThis.location.reload();
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title="Modifier déperdition"
      onClose={onClose}
      portal
      fixSize="middle"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving}>
              {t("Update")}
            </Button>
          </group>
        ),
      }}
    >
      <group
        data-direction="column"
        data-gap="20"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
        data-height="autofit"
      >
        <group data-gap="10" data-space-vertical="20">
          <group data-direction="column" data-gap="20">
            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"Year"}
                  name="envyear"
                  control={control}
                  type="text"
                  size="large"
                />
              </group>
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"Value"}
                  name="envvalue"
                  control={control}
                  type="text"
                  size="large"
                />
              </group>
            </group>
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default YearLossPopup;